import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the core meaning of compassion according to this module?",
    options: [
      "Feeling sorry for others without helping",
      "Deep empathy and a desire to alleviate suffering",
      "Being nice only when it's convenient",
      "Ignoring others' difficulties"
    ],
    correctAnswer: 1,
    explanation: "Compassion involves deep empathy and a genuine desire to alleviate suffering. It's not just feeling sorry, but actively wanting to help and understanding others' pain."
  },
  {
    question: "According to the module, expanding the heart means:",
    options: [
      "Making your physical heart bigger",
      "Developing greater capacity for love, empathy, and understanding",
      "Being more emotional and dramatic",
      "Focusing only on your own feelings"
    ],
    correctAnswer: 1,
    explanation: "Expanding the heart means developing a greater capacity for love, empathy, and understanding. It involves opening yourself to others' experiences and responding with kindness."
  },
  {
    question: "What is the relationship between compassion and self-compassion?",
    options: [
      "They are completely separate",
      "Self-compassion is the foundation for extending compassion to others",
      "Self-compassion prevents you from helping others",
      "You can only have one or the other"
    ],
    correctAnswer: 1,
    explanation: "Self-compassion is the foundation for extending compassion to others. When we treat ourselves with kindness, we naturally become more compassionate toward others."
  },
  {
    question: "The module suggests that compassion can be cultivated through:",
    options: [
      "Only grand gestures of helping",
      "Small acts of kindness and practicing loving-kindness meditation",
      "Being critical of yourself and others",
      "Avoiding difficult emotions"
    ],
    correctAnswer: 1,
    explanation: "Compassion can be cultivated through small acts of kindness and practicing loving-kindness meditation. These everyday practices help expand our capacity for empathy and care."
  },
  {
    question: "What is the benefit of expanding compassion in daily life?",
    options: [
      "It makes relationships more difficult",
      "It creates stronger, more meaningful connections with others",
      "It reduces empathy and understanding",
      "It has no impact on mental health"
    ],
    correctAnswer: 1,
    explanation: "Expanding compassion creates stronger, more meaningful connections with others. When we approach relationships with empathy and understanding, we build deeper and more authentic bonds."
  }
];

const SpiritualModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The essence of compassion</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Expanding your heart capacity</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Self-compassion practices</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by exploring gratitude and seeing life as a gift!</p>
            <p>Ready to cultivate appreciation and thankfulness?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Transcendence – Going Beyond the Ordinary</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule4QuizPage;

